const MAILTO_MAX_LENGTH = 7500;

const formatMonthWithComma = (invoiceMonth) => {
  if (!/^\d{4}-\d{2}$/.test(String(invoiceMonth || ""))) return String(invoiceMonth || "");
  const [year, month] = invoiceMonth.split("-").map(Number);
  const monthName = new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "long" });
  return `${monthName}, ${year}`;
};

const getTimeGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 12) return "Good Morning.";
  if (hour < 17) return "Good Afternoon.";
  return "Good Evening.";
};

const getInvoiceReference = (invoiceNumber) => String(invoiceNumber || "").trim() || "—";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function buildInvoiceMailPlainText({
  invoice,
  senderName,
  senderTitle = "Accounts Head.",
  hasTimesheets = false,
}) {
  const clientName = invoice?.clientName || "Client";
  const monthLabel = formatMonthWithComma(invoice?.invoiceMonth);
  const invoiceReference = getInvoiceReference(invoice?.invoiceNumber);
  const attachmentLine = hasTimesheets
    ? `Please find attached the invoice and the timesheet for the month of ${monthLabel}.  In light of this, we kindly request you to process the payment of invoice - ${invoiceReference} at the earliest.`
    : `Please find attached the invoice for the month of ${monthLabel}.  In light of this, we kindly request you to process the payment of invoice - ${invoiceReference} at the earliest.`;

  return [
    `Hi ${clientName},`,
    "",
    getTimeGreeting(),
    "",
    "",
    attachmentLine,
    "",
    "Your prompt response on this would be greatly appreciated. Please review the attached and let us know if you have questions.",
    "Best regards,",
    "",
    String(senderName || "").trim() || "Accounts Team",
    String(senderTitle || "").trim() || "Accounts Head.",
  ].join("\r\n");
}

export function buildInvoiceMailSubject({ invoice, hasTimesheets = false }) {
  const monthLabel = formatMonthWithComma(invoice?.invoiceMonth);
  if (hasTimesheets) {
    return `Invoice and Timesheet - ${monthLabel}`;
  }
  return `Invoice - ${monthLabel}`;
}

export function buildInvoiceMailHtml(options) {
  const text = options?.body ?? buildInvoiceMailPlainText(options);
  return text
    .split("\r\n")
    .map((line) =>
      line.trim() === ""
        ? `<div style="height:12px;"></div>`
        : `<p style="margin:0 0 4px 0;font-family:Arial,sans-serif;font-size:14px;line-height:1.55;color:#111827;">${escapeHtml(line)}</p>`
    )
    .join("");
}

export function getInvoiceMailAttachmentNames(invoice, attachedTimesheets = []) {
  const invoiceName = `${invoice?.invoiceNumber || "invoice"}.pdf`;
  const timesheetNames = (Array.isArray(attachedTimesheets) ? attachedTimesheets : []).map(
    (entry) => `${entry.employeeName || "Employee"}-${formatMonthWithComma(entry.month || invoice?.invoiceMonth)}-timesheet.pdf`
  );
  return [invoiceName, ...timesheetNames];
}

function buildMailtoHref(to, subject, body) {
  const recipient = String(to || "").trim();
  const prefix = recipient ? `mailto:${encodeURIComponent(recipient)}` : "mailto:";
  return `${prefix}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function buildInvoiceMailtoUrl({
  invoice,
  clientEmail,
  senderName,
  senderTitle,
  attachedTimesheets = [],
}) {
  const hasTimesheets = Array.isArray(attachedTimesheets) && attachedTimesheets.length > 0;
  const subject = buildInvoiceMailSubject({ invoice, hasTimesheets });
  const body = buildInvoiceMailPlainText({
    invoice,
    senderName,
    senderTitle,
    hasTimesheets,
  });

  let href = buildMailtoHref(clientEmail, subject, body);
  if (href.length > MAILTO_MAX_LENGTH) {
    const monthLabel = formatMonthWithComma(invoice?.invoiceMonth);
    const shortBody = [
      `Hi ${invoice?.clientName || "Client"},`,
      "",
      getTimeGreeting(),
      "",
      "",
      `Please find attached the invoice${hasTimesheets ? " and the timesheet" : ""} for the month of ${monthLabel}.`,
      "",
      "Best regards,",
      "",
      String(senderName || "").trim() || "Accounts Team",
      String(senderTitle || "").trim() || "Accounts Head.",
    ].join("\r\n");
    href = buildMailtoHref(clientEmail, subject, shortBody);
  }

  return href;
}

/** Opens the system default mail client with a pre-generated invoice message. */
export function openInvoiceMailto(options) {
  window.location.href = buildInvoiceMailtoUrl(options);
}
